/**
 * Where a support situation goes, and what it is made of — issue #41, Phase 5.
 *
 * `src/sim/supports.ts` owns the mechanism: a set of cells that are STATIC
 * conditionally, released when surviving anchors drop below a threshold. This
 * file is the authoring half — the eight situations, and the generation-time
 * decision about which chunk gets which one.
 *
 * WHY A GENERATOR DECORATOR RATHER THAN A WORLDGEN PASS. Chunks are evicted past
 * 256 resident and regenerate from the seed, so anything stamped into the world
 * *after* generation is lost the moment the player walks two screens away and
 * comes back — the plank would reappear but the group would not, or worse, the
 * group would survive pointing at cells that had been rebuilt. Wrapping the
 * chunk generator puts the stamp inside the same call that builds the chunk, so
 * a regenerated chunk comes back byte-identical *and* re-registers its group,
 * which is the only arrangement in which "pure in the seed" is true.
 *
 * THE ONE CONNECTIVITY GUARANTEE. **A stamp never turns air into matter.** It
 * works entirely inside the ceiling: solid rock above a corridor becomes a held
 * load, and the ceiling row beneath it becomes a pair of brackets with open air
 * between them. Rock does become air in that middle span, which only ever adds
 * headroom — the direction that cannot seal a band or narrow a corridor, so
 * `reachability.test.ts` and `spawnClearance.test.ts` cannot be made worse by
 * this feature. The guarantee is asserted cell-by-cell in
 * `tests/supports.test.ts` against the undecorated generator, because "it should
 * be safe" is how a generator seals a band.
 *
 * PURE IN THE SEED. Which chunk draws a site, and which of the eight it draws,
 * are both `hash2(cx, cy, seed)` — no generation order, no neighbour queries, no
 * clock. Where inside the chunk it lands depends on the chunk's own cells, which
 * the base generator already produced deterministically. `setPiece.ts` makes the
 * same promise for the same reasons.
 */

import { hash2 } from '../core/rng';
import { CHUNK_SHIFT, CHUNK_SIZE, type Chunk, type ChunkGenerator } from '../sim/world';
import { Flag, M, VOID } from '../sim/materials';
import { MAT, type MaterialName } from '../sim/materialData';
import { MAX_ANCHORS, MAX_GROUP_CELLS, type Supports } from '../sim/supports';

/**
 * One authored situation.
 *
 * Deliberately thin. Every field is read by `stampSite` below or by the group it
 * builds — a support that carried a flavour tag nobody consulted would be the
 * fourteenth authored-and-unread field found this session, and
 * `tests/supports.test.ts` scans for exactly that.
 */
export interface SupportSituation {
  /** Stable id; half of the registry key. */
  id: string;
  /**
   * The ticker line when it gives way. This is the *communication* half of the
   * chain (docs/06 §4) — the player has to be able to name what killed them, or
   * a fair death reads as a random one.
   */
  label: string;
  /** The deck: what holds, and what the anchors are made of. */
  deck: MaterialName;
  /** The load: what is held. Must be mobile matter, or nothing ever falls. */
  load: MaterialName;
  /** Deck width in cells. */
  span: number;
  /**
   * Load height in cells. The group holds the two bracket runs as well as the
   * load, so `span × loadH + 2 × BRACKET_W` must fit `MAX_GROUP_CELLS`.
   */
  loadH: number;
  /**
   * Surviving anchors needed, out of `MAX_ANCHORS`.
   *
   * Every bracket cell is an anchor, so this reads directly as "how much of the
   * bracketing has to be left": 5 of 8 is a plank that fails as soon as either
   * end is half gone, 4 of 8 is one that holds until a whole end does.
   */
  threshold: number;
}

/**
 * The eight.
 *
 * Three failure causes between them, which is the point of authoring eight
 * rather than one with eight skins: **heat** (the deck burns or thaws),
 * **corrosion** (the load eats its own support, needing nothing from the player
 * but time and proximity), and **impact** (the deck is struck). A set that all
 * died the same way would be one situation with eight names.
 *
 * The first is the talk's canonical fair death. Issue #44's research note is
 * worth reading before citing it as prior art: it was a hypothetical on a slide
 * reading `COMMUNICATION`, never demonstrated, and Noita's rigid bodies have no
 * fire coupling at all — it is *the outcome the talk argues for*, which we can
 * deliver on terrain cells with no solver, not a thing that shipped there.
 */
export const SUPPORT_SITUATIONS: readonly SupportSituation[] = [
  {
    id: 'plank-over-lava',
    label: 'the plank gives way',
    deck: 'WOOD',
    load: 'LAVA',
    span: 32,
    loadH: 7,
    threshold: 5,
  },
  {
    id: 'scaffold-over-tar',
    label: 'the scaffold burns through',
    deck: 'WOOD',
    load: 'CRUDE_TAR',
    span: 28,
    loadH: 6,
    threshold: 5,
  },
  {
    id: 'ice-shelf-over-water',
    label: 'the ice shelf calves',
    deck: 'ICE',
    load: 'WATER',
    span: 24,
    loadH: 8,
    threshold: 4,
  },
  {
    // Gravel, and not snow, which is where this one started.
    //
    // `thermal.ts` flags any cell below -5 °C with `brittleness > 0.4` as
    // `Flag.FROZEN` — the setup half of the frost-and-shatter combo — and
    // `cellsim.stepCell` returns early on `STATIC | FROZEN`. SNOW spawns at
    // -8 °C with brittleness 1, so a snow load is frozen from the first thermal
    // pass and **cannot fall no matter what the support does**: measured, 216
    // of 224 cells were still in the niche 3000 ticks after the lid gave way.
    // The support worked perfectly and the situation was inert, which is the
    // most expensive kind of authoring mistake because everything reports
    // success. `loads can actually fall` in the test file now derives that rule
    // from `materialData` so the next one is caught at the list.
    id: 'permafrost-lid-over-gravel',
    label: 'the permafrost lid drops',
    deck: 'PERMAFROST',
    load: 'GRAVEL',
    span: 26,
    loadH: 6,
    threshold: 4,
  },
  {
    id: 'rusted-catwalk-over-acid',
    label: 'the rusted catwalk dissolves',
    deck: 'RUSTED_IRON',
    load: 'ACID',
    span: 24,
    loadH: 6,
    threshold: 4,
  },
  {
    id: 'limestone-lip-over-acid',
    label: 'the limestone lip crumbles',
    deck: 'LIMESTONE',
    load: 'ACID',
    span: 20,
    loadH: 8,
    threshold: 4,
  },
  {
    id: 'glass-pane-over-sand',
    label: 'the glass pane shatters',
    deck: 'GLASS',
    load: 'SAND',
    span: 20,
    loadH: 8,
    threshold: 4,
  },
  {
    id: 'bone-arch-over-grave-soil',
    label: 'the bone arch collapses',
    deck: 'BONE',
    load: 'GRAVE_SOIL',
    span: 22,
    loadH: 6,
    threshold: 4,
  },
];

/**
 * One chunk in `SITE_PERIOD` draws a site.
 *
 * 4, and not tuned: `CellWorld.updateActive` evicts past 256 resident chunks and
 * `Supports` caps the registry at 64, so one site per four chunks is exactly the
 * density at which a fully resident world fills the registry and no further. A
 * denser setting would silently discard placements; a sparser one would leave
 * the cap doing nothing.
 */
export const SITE_PERIOD = 4;

/** Open rows a site needs beneath the deck, so the load falls somewhere. */
const CLEAR_BELOW = 6;

/**
 * Deck cells at each end of the span. Everything between them is open air.
 *
 * THE LOAD MUST OVERHANG, and this constant is the whole reason the feature is
 * not decorative. The first draft made the deck a solid row under a solid load
 * in a sealed rock niche, and a run with `Supports.step()` stubbed to a no-op
 * *still passed* two of the mechanism tests: the load was resting on the deck,
 * so ordinary collision was holding it and STATIC was doing nothing. A support
 * group only means something where there is nothing underneath — so the deck is
 * a pair of brackets, the middle of the span is open to the corridor, and the
 * cells hanging over that gap are held by the flag and by nothing else.
 *
 * 4 per side, which leaves at least half of every authored span unsupported.
 */
const BRACKET_W = 4;

/**
 * Placements attempted inside a chunk before giving up.
 *
 * A site is a *candidate*, not a guarantee: a chunk of solid rock or open sky
 * has nowhere to put one, and forcing it would mean carving geometry the
 * generator did not intend. Bounded so generation cost stays flat.
 */
const PROBE_TRIES = 24;

/**
 * Which situation this chunk draws, or null. Pure in `(seed, cx, cy)` and
 * answerable without generating anything, which is what makes the placement
 * auditable — see the determinism fixtures.
 */
export function siteSituationFor(seed: number, cx: number, cy: number): SupportSituation | null {
  const h = hash2(cx, cy, seed);
  if (h % SITE_PERIOD !== 0) return null;
  return SUPPORT_SITUATIONS[(h >>> 9) % SUPPORT_SITUATIONS.length];
}

function solidAt(chunk: Chunk, x: number, y: number): boolean {
  return M.solid[chunk.mat[(y << CHUNK_SHIFT) | x]] === 1;
}

/**
 * Stamp a situation into a freshly generated chunk and register its group.
 *
 * Returns the situation actually placed, or null — a chunk that drew a site but
 * had nowhere to put it is normal, and the registry must reflect what is in the
 * world rather than what was rolled.
 */
export function stampSupportSite(
  chunk: Chunk,
  cx: number,
  cy: number,
  seed: number,
  supports: Supports,
): SupportSituation | null {
  const sit = siteSituationFor(seed, cx, cy);
  if (sit === null) return null;

  const span = sit.span;
  const loadH = sit.loadH;
  const xRange = CHUNK_SIZE - span - 2;
  const yRange = CHUNK_SIZE - loadH - CLEAR_BELOW - 2;
  // The group holds the load rect plus the two bracket runs.
  if (xRange < 1 || yRange < 1 || span * loadH + 2 * BRACKET_W > MAX_GROUP_CELLS) return null;
  if (span < 4 * BRACKET_W) return null;

  for (let t = 0; t < PROBE_TRIES; t++) {
    const h = hash2(cx * 0x2f1b + t, cy * 0x9e37 + t, seed ^ 0x5bf03635);
    const x0 = 1 + (h % xRange);
    const y = loadH + 1 + ((h >>> 11) % yRange);

    // The ceiling row must be solid across the whole span…
    let ok = true;
    for (let x = x0; x < x0 + span && ok; x++) ok = solidAt(chunk, x, y);
    // …with open space beneath it for the load to fall into…
    for (let d = 1; d <= CLEAR_BELOW && ok; d++) {
      for (let x = x0; x < x0 + span && ok; x++) {
        ok = chunk.mat[((y + d) << CHUNK_SHIFT) | x] === VOID;
      }
    }
    // …and solid rock above it to hollow out. Nothing here reads a cell outside
    // the chunk, so the stamp can never depend on a neighbour's generation.
    for (let d = 1; d <= loadH && ok; d++) {
      for (let x = x0; x < x0 + span && ok; x++) ok = solidAt(chunk, x, y - d);
    }
    if (!ok) continue;

    const deck = MAT[sit.deck];
    const load = MAT[sit.load];
    const cells: number[] = [];
    const baseX = cx << CHUNK_SHIFT;
    const baseY = cy << CHUNK_SHIFT;

    // The load is written **without** STATIC, and the group applies it on its
    // first step (see the ARMING note in `supports.ts`). Stamping it STATIC here
    // would make `getChunk`'s recount see nothing mobile in the chunk, clear the
    // dirty rects and put it to sleep — a lava pocket that never conducts a
    // degree into the plank under it. The load must be loose for exactly as long
    // as it takes the chunk to be counted, and not one tick longer.
    for (let d = 1; d <= loadH; d++) {
      const ly = y - d;
      for (let x = x0; x < x0 + span; x++) {
        const i = (ly << CHUNK_SHIFT) | x;
        chunk.mat[i] = load;
        chunk.temp[i] = M.spawnTemp[load];
        chunk.flags[i] = 0;
        chunk.aux[i] = 0;
        cells.push(baseX + x, baseY + ly);
      }
    }
    // The deck: brackets at both ends, open air between them, so the middle of
    // the load is held by the flag and by nothing else. See `BRACKET_W`.
    //
    // The brackets join the group, which is not cosmetic and was found by
    // measurement: a plank that burns does not become air, it becomes ASH — and
    // `transmute` carries the cell's flags across, so the residue inherits the
    // deck's STATIC and plugs the hole. The `bone-arch-over-grave-soil` fixture
    // failed exactly that way, with a wall of soil sitting on a static ash lid
    // over an open corridor. A group that releases the load but not the thing
    // that was holding it is half a collapse.
    const anchors: number[] = [];
    for (let b = 0; b < 2; b++) {
      const bx = b === 0 ? x0 : x0 + span - BRACKET_W;
      for (let k = 0; k < BRACKET_W; k++) {
        const x = bx + k;
        const i = (y << CHUNK_SHIFT) | x;
        chunk.mat[i] = deck;
        chunk.temp[i] = M.spawnTemp[deck];
        chunk.flags[i] = Flag.STATIC;
        chunk.aux[i] = 0;
        cells.push(baseX + x, baseY + y);
        // Every bracket cell is an anchor: 2 x BRACKET_W is 8, which is
        // MAX_ANCHORS, so the sample is the whole support rather than a guess
        // at where it will fail.
        anchors.push(baseX + x, baseY + y);
      }
    }
    // The unsupported middle. Rock becomes air, which can only ever add
    // headroom to a corridor — the direction that cannot seal a band.
    for (let x = x0 + BRACKET_W; x < x0 + span - BRACKET_W; x++) {
      const i = (y << CHUNK_SHIFT) | x;
      chunk.mat[i] = VOID;
      chunk.temp[i] = 20;
      chunk.flags[i] = 0;
      chunk.aux[i] = 0;
    }

    supports.register({
      id: `${cx},${cy}:${sit.id}`,
      label: sit.label,
      anchorMat: deck,
      threshold: sit.threshold,
      anchors,
      cells,
    });
    return sit;
  }
  return null;
}

/**
 * Wrap a chunk generator so every chunk it builds gets its support site, if it
 * drew one. This is the whole integration surface: `game.ts` passes
 * `worldGen.generator` through here on the way to `new CellWorld(...)`.
 */
export function withSupportSites(
  base: ChunkGenerator,
  seed: number,
  supports: Supports,
): ChunkGenerator {
  return (chunk: Chunk, cx: number, cy: number): void => {
    base(chunk, cx, cy);
    stampSupportSite(chunk, cx, cy, seed, supports);
  };
}
